// ============================================
// NRS.01 — Captain Result Listener Service
// ============================================
// Refactored from: src/services/captain/captainPipeline.ts
// Spec: hlx-nrs-01-captain-result-listener.md (LOCKED)
// Contract: onSnapshot subscription, schema validation, status routing,
//           Firestore-backed idempotency, health monitor (5-min threshold),
//           clinical sanity check
// ============================================

import type {
  CaptainListenerConfig,
  SchemaValidationResult,
  SchemaValidationError,
  SanityCheckResult,
  SanityCheckFailure,
  LabResultInjection,
  ListenerHealthStatus,
  ListenerHealthState,
  CaptainListenerMetrics,
  FirestoreProvider,
  DisconnectReason,
  ListenerHealthAlert,
  CorrectionDocument,
} from '../types/captainResultListener';
import { DEFAULT_LISTENER_CONFIG } from '../types/captainResultListener';
import type { CaptainResultDocument, CaptainResponse } from '../../shared/captainPipeline';
import { CLINICAL_SANITY_RANGES } from '../config/clinicalSanityRanges';

// ── Metrics State ──

let _metrics: CaptainListenerMetrics = {
  totalCalls: 0,
  successRate: 1,
  avgLatencyMs: 0,
  errorsByType: {},
  accuracyOnGoldStandard: 0,
};
let _totalLatency = 0;
let _successCount = 0;

// ── Schema Validation ──

export function validateCaptainResultDocument(doc: unknown): SchemaValidationResult {
  const errors: SchemaValidationError[] = [];

  if (!doc || typeof doc !== 'object') {
    return { valid: false, errors: [{ field: 'root', expected: 'object', actual: typeof doc, message: 'Document must be an object' }], document: null };
  }

  const d = doc as Record<string, unknown>;

  // Required fields
  const requiredStrings = ['sessionId', 'patientId'];
  for (const field of requiredStrings) {
    if (typeof d[field] !== 'string' || !(d[field] as string).trim()) {
      errors.push({ field, expected: 'non-empty string', actual: String(d[field]), message: `${field} must be a non-empty string` });
    }
  }

  // createdAt — required (NRS.01 D#2 c1)
  if (d.createdAt === undefined || d.createdAt === null) {
    errors.push({ field: 'createdAt', expected: 'timestamp or date', actual: String(d.createdAt), message: 'createdAt is required' });
  }

  // extractionStatus enum
  const validStatuses = ['complete', 'partial', 'failed'];
  if (!validStatuses.includes(d.extractionStatus as string)) {
    errors.push({ field: 'extractionStatus', expected: 'complete|partial|failed', actual: String(d.extractionStatus), message: 'extractionStatus must be complete, partial, or failed' });
  }

  // captainResponse object
  if (d.extractionStatus !== 'failed') {
    if (!d.captainResponse || typeof d.captainResponse !== 'object') {
      errors.push({ field: 'captainResponse', expected: 'object', actual: typeof d.captainResponse, message: 'captainResponse required when not failed' });
    } else {
      const cr = d.captainResponse as Record<string, unknown>;
      if (!Array.isArray(cr.biomarkers)) {
        errors.push({ field: 'captainResponse.biomarkers', expected: 'array', actual: typeof cr.biomarkers, message: 'biomarkers must be an array' });
      }
      if (typeof cr.schemaVersion !== 'string') {
        errors.push({ field: 'captainResponse.schemaVersion', expected: 'string', actual: typeof cr.schemaVersion, message: 'schemaVersion required' });
      }
    }
  }

  // failureReason when failed
  if (d.extractionStatus === 'failed' && typeof d.failureReason !== 'string') {
    errors.push({ field: 'failureReason', expected: 'string', actual: typeof d.failureReason, message: 'failureReason required when extractionStatus is failed' });
  }

  return {
    valid: errors.length === 0,
    errors,
    document: errors.length === 0 ? d as unknown as CaptainResultDocument : null,
  };
}

// ── Clinical Sanity Check ──

export function runClinicalSanityCheck(captainResponse: CaptainResponse): SanityCheckResult {
  const failures: SanityCheckFailure[] = [];

  for (const biomarker of captainResponse.biomarkers) {
    const range = CLINICAL_SANITY_RANGES.find(
      (r) => r.biomarkerName.toLowerCase() === biomarker.name.toLowerCase(),
    );
    
    if (!range) {
      // Red Team V15: Undefined biomarker must hard block and route to operator
      failures.push({
        biomarkerName: biomarker.name,
        value: biomarker.value,
        unit: biomarker.unit,
        expectedRange: { min: -1, max: -1 }, // Magic value for "undefined range"
        message: `Biomarker "${biomarker.name}" has no defined clinical sanity range. Blocked for operator review.`,
      });
      continue;
    }

    if (biomarker.value < range.min || biomarker.value > range.max) {
      failures.push({
        biomarkerName: biomarker.name,
        value: biomarker.value,
        unit: biomarker.unit,
        expectedRange: { min: range.min, max: range.max },
        message: `${biomarker.name} value ${biomarker.value} ${biomarker.unit} outside physically possible range (${range.min}-${range.max})`,
      });
    }
  }

  return { passed: failures.length === 0, failures };
}

// ── Status Routing ──

export type StatusRouteAction = 'inject_full' | 'inject_partial' | 'display_error' | 'reject_schema';

export function routeByStatus(
  extractionStatus: string,
  schemaValid: boolean,
  sanityPassed: boolean,
): StatusRouteAction {
  if (!schemaValid) return 'reject_schema';
  if (extractionStatus === 'failed') return 'display_error';
  if (extractionStatus === 'partial') return 'inject_partial';
  if (extractionStatus === 'complete' && sanityPassed) return 'inject_full';
  if (extractionStatus === 'complete' && !sanityPassed) return 'inject_partial'; // Treat sanity failures as partial
  return 'reject_schema';
}

// ── Listener Service ──

// NRS.01 D#1 c5: Static registry ensures at most one active listener per sessionId
const _activeListeners = new Map<string, CaptainResultListenerService>();

export class CaptainResultListenerService {
  private config: CaptainListenerConfig;
  private firestoreProvider: FirestoreProvider;
  private unsubscribe: (() => void) | null = null;
  private healthState: ListenerHealthState = 'disconnected';
  private lastEventAt: Date | null = null;
  private healthCheckTimer: ReturnType<typeof setInterval> | null = null;
  private totalEventsProcessed = 0;
  private duplicatesSkipped = 0;
  private errorsEncountered = 0;
  private onInjection: ((injection: LabResultInjection) => void) | null = null;
  private onError: ((error: string, details?: Record<string, unknown>) => void) | null = null;
  private onHealthAlert: ((alert: ListenerHealthAlert) => void) | null = null;

  // XMO.09 D#3: Corrections delivery state
  private correctionsUnsubscribe: (() => void) | null = null;
  private appliedCorrections: Map<string, CorrectionDocument> = new Map();
  private currentCaptainResponse: CaptainResponse | null = null;
  private onCorrectionApplied: ((correctedResponse: CaptainResponse, correction: CorrectionDocument) => void) | null = null;

  // NRS.01 D#1 c2: Auth recovery state
  private isReauthenticating = false;
  private authRetryCount = 0;
  private static readonly MAX_AUTH_RETRIES = 3;

  // NRS.01 D#1 c4: Consecutive disconnect tracking for health alerts
  private consecutiveDisconnects = 0;
  private lastDisconnectAt: Date | null = null;

  constructor(
    config: Partial<CaptainListenerConfig> & Pick<CaptainListenerConfig, 'patientId' | 'sessionId'>,
    firestoreProvider: FirestoreProvider,
  ) {
    this.config = { ...DEFAULT_LISTENER_CONFIG, ...config };
    this.firestoreProvider = firestoreProvider;
  }

  /** Set callback for successful lab result injections */
  onLabResultInjection(callback: (injection: LabResultInjection) => void): void {
    this.onInjection = callback;
  }

  /** Set callback for errors */
  onListenerError(callback: (error: string, details?: Record<string, unknown>) => void): void {
    this.onError = callback;
  }

  /** NRS.01 D#1 c4: Set callback for health alerts on repeated disconnections */
  onHealthAlertCallback(callback: (alert: ListenerHealthAlert) => void): void {
    this.onHealthAlert = callback;
  }

  /**
   * XMO.09 D#3 c4: Set callback for when corrections are applied.
   * NRS.07 uses this to re-run the explanation pipeline on corrected values.
   */
  onCorrectionAppliedCallback(callback: (correctedResponse: CaptainResponse, correction: CorrectionDocument) => void): void {
    this.onCorrectionApplied = callback;
  }

  /**
   * XMO.09 D#3 c2: Get the current in-memory CaptainResponse with all corrections merged.
   * Returns null if no captain response has been received yet.
   */
  getCorrectedCaptainResponse(): CaptainResponse | null {
    return this.currentCaptainResponse;
  }

  /**
   * NRS.01 D#1 c1: Start listening for captain results.
   * Must only be called when a valid session exists (not on page load).
   * c5: Enforces singleton per sessionId — tears down any existing listener first.
   */
  start(): void {
    // c1: Validate session exists before subscribing
    if (!this.config.sessionId || !this.config.sessionId.trim()) {
      throw new Error('[NRS.01] Cannot start listener: no valid sessionId');
    }
    if (!this.config.patientId || !this.config.patientId.trim()) {
      throw new Error('[NRS.01] Cannot start listener: no valid patientId');
    }

    // c5: If this instance already has an active listener, skip
    if (this.unsubscribe) return;

    // c5: Tear down any existing listener for this sessionId (cross-instance singleton)
    const existing = _activeListeners.get(this.config.sessionId);
    if (existing && existing !== this) {
      existing.stop();
    }
    _activeListeners.set(this.config.sessionId, this);

    this.subscribe();
  }

  /**
   * NRS.01 D#1 c3: Stop listening — called on session end, navigation away, or timeout.
   */
  stop(reason?: DisconnectReason): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    // XMO.09 D#3: Tear down corrections listener
    if (this.correctionsUnsubscribe) {
      this.correctionsUnsubscribe();
      this.correctionsUnsubscribe = null;
    }
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
    this.healthState = 'disconnected';

    // c5: Remove from singleton registry
    if (_activeListeners.get(this.config.sessionId) === this) {
      _activeListeners.delete(this.config.sessionId);
    }

    // c4: Track disconnects for health alerts (skip manual/session_end — those are intentional)
    if (reason && reason !== 'manual' && reason !== 'session_end') {
      this.consecutiveDisconnects++;
      this.lastDisconnectAt = new Date();
      if (this.consecutiveDisconnects >= 2 && this.onHealthAlert) {
        this.onHealthAlert({
          sessionId: this.config.sessionId,
          consecutiveDisconnects: this.consecutiveDisconnects,
          lastDisconnectAt: this.lastDisconnectAt,
          reason: `Repeated disconnect (${reason}): ${this.consecutiveDisconnects} consecutive`,
        });
      }
    } else if (reason === 'session_end' || reason === 'manual') {
      // Intentional stop — reset disconnect counter
      this.consecutiveDisconnects = 0;
    }
  }

  /** Get current health status */
  getHealthStatus(): ListenerHealthStatus {
    return {
      state: this.healthState,
      lastEventAt: this.lastEventAt,
      lastHealthCheckAt: new Date(),
      disconnectedSinceMs: this.healthState === 'disconnected' || this.healthState === 'stale'
        ? this.lastEventAt ? Date.now() - this.lastEventAt.getTime() : null
        : null,
      totalEventsProcessed: this.totalEventsProcessed,
      duplicatesSkipped: this.duplicatesSkipped,
      errorsEncountered: this.errorsEncountered,
    };
  }

  /** Get metrics */
  getMetrics(): CaptainListenerMetrics {
    return {
      ..._metrics,
      totalCalls: this.totalEventsProcessed,
      successRate: this.totalEventsProcessed > 0
        ? _successCount / this.totalEventsProcessed
        : 1,
      avgLatencyMs: this.totalEventsProcessed > 0
        ? _totalLatency / this.totalEventsProcessed
        : 0,
    };
  }

  /** Check if the listener is currently active */
  isActive(): boolean {
    return this.unsubscribe !== null;
  }

  // ── Private Methods ──

  /** Internal subscribe — factored out so resubscribe can call it after auth recovery */
  private subscribe(): void {
    this.unsubscribe = this.firestoreProvider.onSnapshot(
      this.config.collectionPath,
      { field: 'patientId', value: this.config.patientId },
      (doc) => this.handleDocument(doc),
      (error) => this.handleSnapshotError(error),
    );

    this.healthState = 'connected';
    this.consecutiveDisconnects = 0; // Reset on successful connect
    this.authRetryCount = 0;
    this.startHealthMonitor();
  }

  /**
   * NRS.01 D#1 c2: Handle onSnapshot errors with auth recovery.
   * Detects UNAUTHENTICATED/permission-denied, re-authenticates, re-subscribes.
   */
  private handleSnapshotError(error: Error): void {
    const errorMsg = error.message?.toLowerCase() ?? '';
    const isAuthError =
      errorMsg.includes('unauthenticated') ||
      errorMsg.includes('permission-denied') ||
      errorMsg.includes('permission denied') ||
      errorMsg.includes('missing or insufficient permissions') ||
      (error as { code?: string }).code === 'permission-denied' ||
      (error as { code?: string }).code === 'unauthenticated';

    if (isAuthError && this.firestoreProvider.reauthenticate) {
      this.handleAuthRecovery();
    } else {
      this.handleError(error);
    }
  }

  /**
   * NRS.01 D#1 c2: Attempt re-authentication and re-subscription.
   * Retries up to MAX_AUTH_RETRIES before giving up.
   */
  private async handleAuthRecovery(): Promise<void> {
    if (this.isReauthenticating) return;
    if (this.authRetryCount >= CaptainResultListenerService.MAX_AUTH_RETRIES) {
      this.onError?.('Auth recovery exhausted', {
        retries: this.authRetryCount,
        sessionId: this.config.sessionId,
      });
      this.stop('auth_expired');
      return;
    }

    this.isReauthenticating = true;
    this.authRetryCount++;

    // Tear down current listener before re-auth
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }

    try {
      const success = await this.firestoreProvider.reauthenticate!();
      if (success) {
        // Re-subscribe with fresh auth
        this.subscribe();
      } else {
        this.onError?.('Re-authentication failed', { attempt: this.authRetryCount });
        this.stop('auth_expired');
      }
    } catch (err) {
      this.onError?.('Re-authentication threw', { error: String(err), attempt: this.authRetryCount });
      this.stop('auth_expired');
    } finally {
      this.isReauthenticating = false;
    }
  }

  private async handleDocument(rawDoc: CaptainResultDocument): Promise<void> {
    const startTime = Date.now();
    _metrics.totalCalls++;
    this.lastEventAt = new Date();
    this.healthState = 'connected';

    try {
      // Step 1: Schema validation (NRS.01 D#2)
      const validation = validateCaptainResultDocument(rawDoc);
      if (!validation.valid || !validation.document) {
        this.errorsEncountered++;
        _metrics.errorsByType['schema_validation'] = (_metrics.errorsByType['schema_validation'] || 0) + 1;

        // NRS.01 D#2 c4: Log warn with sessionId + missing fields, do not crash
        // Use this.config.sessionId (guaranteed valid from start()) — not an unsafe cast on the raw doc
        const missingFields = validation.errors.map((e) => e.field);
        console.warn(`[NRS.01 D#2] Schema validation failed — sessionId=${this.config.sessionId}, missing/invalid fields: [${missingFields.join(', ')}]`);

        this.onError?.('Schema validation failed', { errors: validation.errors });
        // c5: Malformed document is skipped (not retried)
        return;
      }

      const doc = validation.document;

      // Step 2: Idempotency check
      const existing = await this.firestoreProvider.getProcessedRecord(
        this.config.patientId,
        doc.sessionId,
      );
      if (existing && existing.status === 'injected') {
        this.duplicatesSkipped++;
        return;
      }

      // Step 3: Handle by extraction status
      if (doc.extractionStatus === 'failed') {
        this.onError?.('Extraction failed', { failureReason: doc.failureReason });
        await this.firestoreProvider.setProcessedRecord(this.config.patientId, {
          sessionId: doc.sessionId,
          status: 'failed',
          injectedAt: new Date(),
          extractionStatus: 'failed',
        });
        this.totalEventsProcessed++;
        return;
      }

      // Step 4: Clinical sanity check
      const sanityCheck = this.config.enableSanityCheck
        ? runClinicalSanityCheck(doc.captainResponse)
        : { passed: true, failures: [] };

      // Step 4a: NRS.01 D#5 — Flag implausible values, log to audit, trigger XMO.09
      if (!sanityCheck.passed) {
        // Fire-and-forget: flag + audit + review request (processing continues regardless)
        this.flagClinicalSanityFailure(doc, sanityCheck).catch((err) => {
          console.error('[NRS.01] Clinical sanity flag/audit write failed (non-blocking)', err);
        });
      }

      // Step 5: Route by status
      const action = routeByStatus(doc.extractionStatus, true, sanityCheck.passed);

      // Step 6: XMO.09 D#3 c2 — Store CaptainResponse in memory for corrections merge
      this.currentCaptainResponse = { ...doc.captainResponse };

      // Step 6a: Build injection
      const injection: LabResultInjection = {
        sessionId: doc.sessionId,
        patientId: doc.patientId,
        captainResponse: doc.captainResponse,
        extractionStatus: doc.extractionStatus,
        isPartial: action === 'inject_partial',
        failureReason: doc.failureReason,
        sanityCheck,
        injectedAt: new Date(),
      };

      // Step 7: Emit injection
      this.onInjection?.(injection);

      // Step 7a: XMO.09 D#3 c1 — Start corrections listener after first successful injection
      this.startCorrectionsListener();

      // Step 8: Record as processed
      await this.firestoreProvider.setProcessedRecord(this.config.patientId, {
        sessionId: doc.sessionId,
        status: 'injected',
        injectedAt: new Date(),
        extractionStatus: doc.extractionStatus,
      });

      this.totalEventsProcessed++;
      _successCount++;
      _totalLatency += Date.now() - startTime;
    } catch (error) {
      this.errorsEncountered++;
      const errorType = error instanceof Error ? error.constructor.name : 'Unknown';
      _metrics.errorsByType[errorType] = (_metrics.errorsByType[errorType] || 0) + 1;
      this.onError?.('Document processing failed', { error: String(error) });
    }
  }

  /**
   * NRS.01 D#5 — Flag, audit-log, and trigger XMO.09 review for implausible biomarker values.
   * Fire-and-forget: failures here NEVER block patient result delivery.
   */
  private async flagClinicalSanityFailure(
    doc: CaptainResultDocument,
    sanityCheck: SanityCheckResult,
  ): Promise<void> {
    const { sessionId, patientId } = doc;
    const timestamp = new Date().toISOString();

    // c2: Flag the CaptainResultDocument in Firestore
    await this.firestoreProvider.updateClinicalSanityFlag(sessionId, true);

    // c5: Log each implausible biomarker to xmod_security_audit
    for (const failure of sanityCheck.failures) {
      await this.firestoreProvider.logClinicalSanityAudit({
        eventType: 'clinical_sanity_flag',
        patientId,
        sessionId,
        biomarkerName: failure.biomarkerName,
        value: failure.value,
        unit: failure.unit,
        expectedRange: failure.expectedRange,
        message: failure.message,
        timestamp,
      });
    }

    // c2: Trigger XMO.09 operator review
    await this.firestoreProvider.createAnalystReviewRequest({
      sessionId,
      patientId,
      reason: 'clinical_sanity_failure',
      failures: sanityCheck.failures.map((f) => ({
        biomarkerName: f.biomarkerName,
        value: f.value,
        unit: f.unit,
        expectedRange: f.expectedRange,
      })),
      createdAt: timestamp,
    });
  }

  private handleError(error: Error): void {
    this.errorsEncountered++;
    this.healthState = 'error';
    _metrics.errorsByType['listener_error'] = (_metrics.errorsByType['listener_error'] || 0) + 1;
    this.onError?.('Listener error', { message: error.message });
  }

  /**
   * XMO.09 D#3 c1: Subscribe to captain_results/{sessionId}/corrections subcollection.
   * Each correction document updates the in-memory CaptainResponse and notifies NRS.07.
   */
  private startCorrectionsListener(): void {
    // Only start once, and only if the provider supports it
    if (this.correctionsUnsubscribe) return;
    if (!this.firestoreProvider.onCorrectionsSnapshot) return;

    this.correctionsUnsubscribe = this.firestoreProvider.onCorrectionsSnapshot(
      this.config.sessionId,
      (correction) => this.handleCorrection(correction),
      (error) => {
        console.warn('[XMO.09 D#3] Corrections listener error (non-blocking)', error.message);
      },
    );
  }

  /**
   * XMO.09 D#3 c2/c3: Merge a correction into the in-memory CaptainResponse.
   * Corrections override corresponding biomarker values — never mutates Firestore.
   * c4: Emits onCorrectionApplied so NRS.07 can re-run the explanation pipeline.
   *
   * Handles edge cases:
   * - New biomarker not in original report → added to array
   * - Multiple corrections for same biomarker → latest createdAt wins
   * - Invalid correction data → rejected before merge
   * - onSnapshot reconnect batch → deduplicated by correctionId
   */
  private handleCorrection(correction: CorrectionDocument): void {
    if (!this.currentCaptainResponse) {
      console.warn('[XMO.09 D#3] Correction arrived before CaptainResponse — skipping', correction.correctionId);
      return;
    }

    // Validate correction data before merging (guard against corrupt data from Firestore)
    if (typeof correction.correctedValue !== 'number' || !isFinite(correction.correctedValue)) {
      console.warn(`[XMO.09 D#3] Invalid correctedValue for "${correction.biomarkerName}" — rejecting`, correction.correctionId);
      return;
    }
    if (typeof correction.correctedUnit !== 'string' || !correction.correctedUnit.trim()) {
      console.warn(`[XMO.09 D#3] Invalid correctedUnit for "${correction.biomarkerName}" — rejecting`, correction.correctionId);
      return;
    }

    // Deduplicate: skip if this exact correctionId was already applied
    if (this.appliedCorrections.has(correction.correctionId)) return;

    // Timestamp check: if a previous correction for the same biomarker exists with a newer timestamp, skip
    const existingForBiomarker = Array.from(this.appliedCorrections.values()).find(
      (c) => c.biomarkerName.toLowerCase() === correction.biomarkerName.toLowerCase(),
    );
    if (existingForBiomarker) {
      const existingTime = existingForBiomarker.createdAt instanceof Date
        ? existingForBiomarker.createdAt.getTime()
        : new Date(existingForBiomarker.createdAt).getTime();
      const incomingTime = correction.createdAt instanceof Date
        ? correction.createdAt.getTime()
        : new Date(correction.createdAt).getTime();
      if (incomingTime < existingTime) {
        // Older correction for same biomarker — skip (latest already applied)
        this.appliedCorrections.set(correction.correctionId, correction);
        return;
      }
    }

    // c3: Find the matching biomarker and override its value
    const biomarkerIndex = this.currentCaptainResponse.biomarkers.findIndex(
      (b) => b.name.toLowerCase() === correction.biomarkerName.toLowerCase(),
    );

    if (biomarkerIndex === -1) {
      // New biomarker not in original report — add it (e.g., post-hoc test added by analyst)
      this.currentCaptainResponse.biomarkers.push({
        name: correction.biomarkerName,
        value: correction.correctedValue,
        unit: correction.correctedUnit,
        originalUnit: correction.correctedUnit,
        status: 'suboptimal',
        standardRange: { min: 0, max: 0 },
        functionalRange: null,
        rangeSource: 'standard',
        confidence: 1,
      });
    } else {
      // c3: Override the biomarker value in-memory (not Firestore)
      this.currentCaptainResponse.biomarkers[biomarkerIndex] = {
        ...this.currentCaptainResponse.biomarkers[biomarkerIndex],
        value: correction.correctedValue,
        unit: correction.correctedUnit,
      };
    }

    // Track applied corrections
    this.appliedCorrections.set(correction.correctionId, correction);

    // c4: Notify NRS.07 so it can re-run explanation pipeline on corrected values
    this.onCorrectionApplied?.(this.currentCaptainResponse, correction);
  }

  /**
   * NRS.01 D#1 c4: Health monitor checks every 60s.
   * Detects disconnection within 60s and emits health alert on repeated disconnects.
   */
  private startHealthMonitor(): void {
    if (this.healthCheckTimer) return;

    this.healthCheckTimer = setInterval(() => {
      // c4: If listener is not active, that's a disconnection — detect it
      if (!this.unsubscribe) {
        if (this.healthState !== 'disconnected') {
          this.healthState = 'disconnected';
          this.consecutiveDisconnects++;
          this.lastDisconnectAt = new Date();
          this.onError?.('Listener disconnection detected', {
            sessionId: this.config.sessionId,
            consecutiveDisconnects: this.consecutiveDisconnects,
          });

          // c4: Emit health alert on repeated disconnects (2+)
          if (this.consecutiveDisconnects >= 2 && this.onHealthAlert) {
            this.onHealthAlert({
              sessionId: this.config.sessionId,
              consecutiveDisconnects: this.consecutiveDisconnects,
              lastDisconnectAt: this.lastDisconnectAt,
              reason: `Health monitor detected ${this.consecutiveDisconnects} consecutive disconnections`,
            });
          }
        }
        return;
      }

      // Staleness check — no events within threshold
      if (this.lastEventAt) {
        const sinceLastEvent = Date.now() - this.lastEventAt.getTime();
        if (sinceLastEvent > this.config.healthCheckThresholdMs) {
          this.healthState = 'stale';
          this.onError?.('Listener health alert: no events received', {
            lastEventAt: this.lastEventAt.toISOString(),
            thresholdMs: this.config.healthCheckThresholdMs,
            sinceLastEventMs: sinceLastEvent,
          });
        }
      }
    }, 60_000); // c4: Check every 60 seconds — disconnection detected within 60s
  }
}

// ── Factory ──

export function createCaptainResultListener(
  config: Partial<CaptainListenerConfig> & Pick<CaptainListenerConfig, 'patientId' | 'sessionId'>,
  firestoreProvider: FirestoreProvider,
): CaptainResultListenerService {
  return new CaptainResultListenerService(config, firestoreProvider);
}
